import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, 
  Users, 
  History, 
  Settings, 
  LogOut, 
  ChevronLeft,
  ChevronRight,
  ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/competitors", icon: Users, label: "Competidores" },
  { path: "/history", icon: History, label: "Historial" },
  { path: "/settings", icon: Settings, label: "Configuración" },
];

const adminNavItems = [
  { path: "/admin", icon: ShieldCheck, label: "Administración" },
];

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isSuperadmin = user?.role === "superadmin";
  const items = isSuperadmin ? [...navItems, ...adminNavItems] : navItems;

  return (
    <TooltipProvider delayDuration={0}>
      <aside 
        className={`fixed left-0 top-0 h-full bg-slate-950/90 backdrop-blur-xl border-r border-white/5 z-50 sidebar-transition ${
          collapsed ? "w-20" : "w-64"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center gap-3 p-6 border-b border-white/5 ${collapsed ? "justify-center" : ""}`}>
            <img
              src="/logo.png"
              alt="adsFunnel"
              className="w-10 h-10 object-contain"
              data-testid="sidebar-logo"
            />
            {!collapsed && (
              <div className="animate-fade-in">
                <h1 className="font-bold text-lg text-white tracking-tight">
                  ads<span className="brand-gradient-text">Funnel</span>
                </h1>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest">Ads Intelligence</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;

              const linkContent = (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-cyan-950/30 text-cyan-400 border-r-2 border-cyan-500"
                      : "text-slate-400 hover:text-cyan-400 hover:bg-cyan-950/20"
                  } ${collapsed ? "justify-center px-3" : ""}`}
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && (
                    <span className="text-sm font-medium animate-fade-in">{item.label}</span>
                  )}
                </NavLink>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right" className="bg-slate-900 border-white/10 text-white">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return linkContent;
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-white/5">
            {user && (
              <div className={`flex items-center gap-3 mb-4 ${collapsed ? "justify-center" : ""}`}>
                <Avatar className="w-10 h-10 border border-white/10">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-cyan-950 text-cyan-400">
                    {user.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 min-w-0 animate-fade-in">
                    <p className="text-sm font-medium text-white truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2">
              {collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={logout}
                      className="w-full text-slate-400 hover:text-red-400 hover:bg-red-950/20"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-5 h-5" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-slate-900 border-white/10 text-white">
                    Cerrar Sesión
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-950/20"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Cerrar Sesión
                </Button>
              )}
            </div>
          </div>

          {/* Collapse Toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute -right-3 top-20 w-6 h-6 bg-slate-900 border border-white/10 rounded-full flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
